import {
  findPet,
  PET_MAX_EVOLUTION,
  PET_MAX_PROJECTILES_PER_SECOND,
  petAttackSpeedBonusBps,
  petDamageBonusBps,
} from '@kenocyber/shared';
import { HUNT_BALANCE } from './balance';

/**
 * Every tunable number a combat pet has.
 *
 * Its own file, deliberately: `HUNT_BALANCE` is the ship's economy and a pet
 * must not be able to move it by accident. Nothing here changes an existing
 * value - the pet is additive, and if every line below were deleted the hunt
 * would behave exactly as it did before pets existed.
 *
 * ## How the damage scales
 *
 * A pet's damage is a PERCENTAGE OF THE SHIP'S OWN per-projectile damage, not
 * a flat number. That is the only formulation that keeps a pet useful at
 * level 5 and still useful at level 500 without a second progression curve to
 * maintain - and it means a pet can never outgrow the ship, which is what
 * stops the final form replacing the ship, the cannon and the artifacts.
 *
 * ## Training
 *
 * Foods are ADDITIVE percentages of the pet's own base, applied once:
 *
 *     damage  = shipShot x fraction x (1 + damageFoodCount x 1%)
 *     cadence = baseVolleys        x (1 + speedFoodCount  x 0.5%)
 *
 * Never multiplied together into a compounding number nobody can predict, and
 * never applied to the ship. Cadence is clamped by a documented technical
 * ceiling, `PET_MAX_PROJECTILES_PER_SECOND` - not a balance decision, a limit
 * on how many entities one 16 ms tick may create.
 *
 * ## Rounding
 *
 * Damage is a float in the simulation, as the ship's is, so there is no
 * rounding to exploit: a fraction of a small number stays a small number
 * rather than flooring to zero. The REWARD side is where rounding would
 * matter, and the pet does not touch it - a kill it causes runs through the
 * identical cumulative-total path.
 */

/**
 * The blast of an EXPLOSIVE pet, as data.
 *
 * Provisional, and centralised here rather than beside the model so a balance
 * pass never has to open a renderer. The radii are arena units measured from
 * the point of impact.
 */
export interface PetBlastProfile {
  /**
   * Area damage as a fraction of the direct hit.
   *
   * Applied to every OTHER live enemy in the radius. The enemy that was hit
   * directly does not receive it - see `explosionSplashTargets` in the
   * simulation for why that is a rule and not an optimisation.
   */
  readonly splashFractionOfDirect: number;
  /** Radius per evolution, arena units, from evolution 1. */
  readonly radiusByEvolution: readonly [number, number, number];
}

/**
 * How a volley distributes itself across the enemies on the field.
 *
 * - `single` - every projectile at the pet's one target. The Orb, which fires
 *   one at a time anyway.
 * - `spread` - drawn WITHOUT replacement, so a volley covers as many different
 *   enemies as it has projectiles. The Abyssal Drone, whose blasts overlap and
 *   would be wasted stacked on one corpse.
 * - `independent` - one draw per projectile, WITH replacement, so a volley may
 *   concentrate on one enemy or scatter. The Ballistic Drone: each missile
 *   picks for itself, and A/A/A is as legitimate an outcome as A/B/C.
 * - `ranked` - gun N takes the Nth enemy by the ship's own priority, with no
 *   randomness at all. The Apache Omega: three guns cover the three enemies
 *   closest to the bottom line, and if there are fewer enemies than guns the
 *   spare ones double up on the first. A gunship picks its targets; it does
 *   not draw them.
 */
export type PetTargetingMode = 'single' | 'spread' | 'independent' | 'ranked';

/**
 * How a guided missile flies.
 *
 * Slow off the rail and fast at the end, which is what makes the pursuit curve
 * legible: a projectile that leaves at full speed is a straight line by the
 * time anyone looks at it.
 */
export interface PetMissileProfile {
  /** Speed the moment it leaves the launcher, arena units per second. */
  readonly launchSpeed: number;
  /** Speed it settles at once the boost is spent. */
  readonly maxSpeed: number;
  /** How long the boost lasts, seconds. */
  readonly accelerationSeconds: number;
  /** How long it may fly before it expires harmlessly, seconds. */
  readonly maxLifeSeconds: number;
  /** How sharply it may turn while chasing, radians per second. */
  readonly maxTurnRadPerSecond: number;
}

/**
 * A CONTINUOUS BEAM, which exactly one pet in the game has.
 *
 * The Void Butterfly is the single authorised exception to the project's
 * permanent prohibition on lasers - `docs/PETS_V1_SPEC.md` grants it to that
 * pet and to nothing else. Every other shot, present or future, is a discrete
 * projectile.
 *
 * What evolution buys here is FREQUENCY, never a second beam: the pet emits one
 * beam in all three forms and applies more damage instances per second as it
 * grows. Each instance is a real, separate, authoritative hit with its own
 * damage number, resolved through the same `applyHit` the ship's bolts use.
 */
export interface PetBeamProfile {
  /** Damage instances per second per evolution, before training. */
  readonly instancesPerSecondByEvolution: readonly [number, number, number];
  /**
   * How long the beam stays dark after losing its enemy, milliseconds.
   *
   * Not a cooldown on damage - a cooldown on ACQUISITION. Without it the beam
   * re-locks on the frame the old target died and the player sees a strobe of
   * one-frame beams flicking between corpses.
   */
  readonly reacquireDelayMs: number;
}

/** Everything one pet's combat differs from another's by. */
export interface PetCombatProfile {
  /** Volleys per second, before training. Every cannon fires once per volley. */
  readonly volleysPerSecond: number;
  /** Damage of one projectile, as a fraction of one SHIP projectile. */
  readonly damageFractionOfShipProjectile: number;
  /** Null for a pet whose shots do not explode. */
  readonly blast: PetBlastProfile | null;
  /** How the volley picks its enemies. */
  readonly targeting: PetTargetingMode;
  /** Null for a pet whose shots fly at the ship's own projectile speed. */
  readonly missile: PetMissileProfile | null;
  /** Non-null for the ONE pet that fires a beam instead of projectiles. */
  readonly beam: PetBeamProfile | null;
  /**
   * How many projectiles ONE gun releases per volley.
   *
   * One for every pet whose emitters fire a single shot each. The Apache Omega
   * is three: its guns are the cannon 3 concept, so a gun puts three parallel
   * rounds on the enemy it chose rather than one. That is why its forms deliver
   * 3, 6 and 9 a volley from 1, 2 and 3 guns - the gun count is what grows, and
   * the burst is what makes each gun worth having.
   */
  readonly projectilesPerGun: number;
}

/**
 * Per-pet combat values, in ONE table.
 *
 * A second pet is a second set of numbers, not a second code path: the
 * simulation reads this by definition id and fires, resolves and rewards
 * through exactly the paths the first pet already used. Adding the remaining
 * three pets of `docs/PETS_V1_SPEC.md` is three more entries here.
 *
 * All PROVISIONAL. None of them is a design decision that has been taken.
 */
export const PET_COMBAT_PROFILES: Readonly<Record<string, PetCombatProfile>> = Object.freeze({
  /** 1-star Sentinel Orb: small, fast, single-target bolts. */
  pet_tech_sphere_legendary: Object.freeze({
    volleysPerSecond: 1.4,
    damageFractionOfShipProjectile: 0.22,
    blast: null,
    targeting: 'single',
    missile: null,
    beam: null,
    projectilesPerGun: 1,
  }),
  /**
   * 2-star Abyssal Drone: slower, harder, and it clears crowds.
   *
   * Hits a little harder per ball than the Orb does per bolt and fires a little
   * slower, so single-target output is close - what the Drone actually buys is
   * the splash, which is worth nothing against a lone boss and a great deal
   * against a wave.
   */
  pet_alien_drone_excellent: Object.freeze({
    volleysPerSecond: 1.1,
    damageFractionOfShipProjectile: 0.24,
    blast: Object.freeze({
      splashFractionOfDirect: 0.55,
      radiusByEvolution: Object.freeze([52, 68, 88]) as unknown as readonly [
        number,
        number,
        number,
      ],
    }),
    targeting: 'spread',
    missile: null,
    beam: null,
    projectilesPerGun: 1,
  }),
  /**
   * 3-star Ballistic Drone: fewer shots, harder, guided, strictly single-target.
   *
   * Hits hardest per shot of the three and fires slowest. Its missiles pick
   * their enemies INDEPENDENTLY, so a volley may land entirely on one mob or
   * scatter across four - and none of them damages a bystander. That absence of
   * splash is not an omission: it is the whole difference between this pet and
   * the Abyssal Drone, and the reason the two are worth owning separately.
   */
  pet_missile_drone_perfect: Object.freeze({
    volleysPerSecond: 0.85,
    damageFractionOfShipProjectile: 0.3,
    // No blast. A missile damages the enemy it struck and nobody else.
    blast: null,
    targeting: 'independent',
    missile: Object.freeze({
      launchSpeed: 330,
      maxSpeed: 650,
      accelerationSeconds: 0.35,
      maxLifeSeconds: 3.6,
      /*
       * Deliberately slower than the ship's 4.68.
       *
       * A tight turn rate draws a straight line; the arc is the point of a
       * missile and it needs room to be seen. Slow enough to curve visibly,
       * fast enough that a mob crossing the arena is still caught.
       */
      maxTurnRadPerSecond: 3.2,
    }),
    beam: null,
    projectilesPerGun: 1,
  }),
  /**
   * 4-star Void Butterfly: one continuous laser, and the only one in the game.
   *
   * Damage per instance is the smallest of the four pets by a distance, and it
   * lands six, eight or ten times a second - so the pet trades burst for a
   * steady, uninterruptible stream. Its DPS before training works out at 0.78x,
   * 1.04x and 1.30x one ship projectile per second.
   *
   * `volleysPerSecond` is the instance rate of EV. I and is here only so a
   * caller that has never heard of beams reads something sane; the tick and the
   * Overview both go through `petBeamInstancesPerSecond`, which is per form.
   *
   * No blast, no spread, no missile. The beam damages the one enemy it is
   * locked to and nobody else.
   */
  pet_void_butterfly_anomaly: Object.freeze({
    volleysPerSecond: 6,
    damageFractionOfShipProjectile: 0.13,
    blast: null,
    targeting: 'single',
    missile: null,
    beam: Object.freeze({
      instancesPerSecondByEvolution: Object.freeze([6, 8, 10]) as unknown as readonly [
        number,
        number,
        number,
      ],
      /*
       * A tenth of a second of darkness before the beam may lock again.
       *
       * Long enough that clearing a dense wave does not strobe, short enough
       * that it is never felt as downtime. It costs damage - the accumulator is
       * reset when the lock drops - and that is the honest price of the effect.
       */
      reacquireDelayMs: 100,
    }),
    projectilesPerGun: 1,
  }),
  /**
   * 5-star Apache Omega: a gunship, and the only pet whose guns burst.
   *
   * Each machine gun is the cannon 3 concept - three parallel rounds on ONE
   * enemy - so the forms deliver 3, 6 and 9 projectiles a volley from 1, 2 and
   * 3 guns. Damage per round is the lowest of any pet that fires projectiles,
   * because there are so many of them; the volley rate sits between the Orb's
   * and the Abyssal Drone's.
   *
   * `ranked` targeting, so a gun covers the Nth enemy closest to the bottom
   * line and nothing is drawn at random. No blast, no missile flight, no beam:
   * a round damages the enemy it hit and nobody else.
   */
  pet_apache_prismatic: Object.freeze({
    volleysPerSecond: 1.15,
    damageFractionOfShipProjectile: 0.18,
    blast: null,
    targeting: 'ranked',
    missile: null,
    beam: null,
    projectilesPerGun: 3,
  }),
});

/**
 * The profile for a definition, with a DEFAULT rather than a throw.
 *
 * Throwing here would throw inside the 16 ms tick, which is the worst place
 * for it: a newly seeded or partly deployed pet would stop the simulation
 * instead of fighting slightly wrong.
 */
const DEFAULT_COMBAT_PROFILE: PetCombatProfile = Object.freeze({
  volleysPerSecond: 1.4,
  damageFractionOfShipProjectile: 0.22,
  blast: null,
  targeting: 'single',
  missile: null,
  beam: null,
  projectilesPerGun: 1,
});

export function petCombatProfile(definitionId: string): PetCombatProfile {
  return PET_COMBAT_PROFILES[definitionId] ?? DEFAULT_COMBAT_PROFILE;
}

/**
 * Blast radius of one form, arena units. Zero when the pet does not explode.
 *
 * Zero is the signal the simulation reads: a projectile with no radius resolves
 * exactly as every projectile did before explosions existed.
 */
export function petBlastRadius(definitionId: string, evolution: number): number {
  const blast = petCombatProfile(definitionId).blast;
  if (!blast) {
    return 0;
  }
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  return blast.radiusByEvolution[clamped - 1] ?? 0;
}

/** Area damage one blast deals to every OTHER enemy inside its radius. */
export function petSplashDamage(definitionId: string, directDamage: number): number {
  const blast = petCombatProfile(definitionId).blast;
  return blast ? directDamage * blast.splashFractionOfDirect : 0;
}

export const PET_BALANCE = Object.freeze({
  /**
   * Damage of one pet projectile, as a fraction of one SHIP projectile.
   *
   * The DEFAULT, kept for a definition the table above has never heard of.
   * Every live pet reads its own from `PET_COMBAT_PROFILES`.
   */
  damageFractionOfShipProjectile: 0.22,
  /** Volleys per second. Every emitter fires once per volley. The default. */
  volleysPerSecond: 1.4,
  /** Projectiles are their own entities with their own, faster flight. */
  bullet: Object.freeze({
    speed: HUNT_BALANCE.projectiles.speed * 1.15,
    maxTurnRadPerSecond: HUNT_BALANCE.projectiles.maxTurnRadPerSecond * 0.8,
    hitRadius: HUNT_BALANCE.projectiles.hitRadius,
    despawnMargin: HUNT_BALANCE.projectiles.despawnMargin,
    /** Lateral gap between the projectiles of one volley, arena units. */
    spacing: 7,
  }),
  /**
   * Where the pet flies: a lane in the lower band, to the SIDE of the ship.
   *
   * Deliberately NOT welded to the ship - it drifts along a figure-of-eight
   * under its own power, which is what makes it read as an escort rather than
   * a decoration bolted on.
   *
   * The separation is HORIZONTAL, and that is forced by the arena: the ship
   * sits at 90% of the height with a 96-unit sprite, leaving 24 units below it
   * - not a lane, a sliver. A test asserts the two boxes are disjoint, which
   * is the real requirement: the pet must never cover the ship, a cannon or
   * the HUD.
   */
  /*
   * The lane is inset by the largest form's VISUAL REACH.
   *
   * The MK III's technological circle extends about 1.78 model units on a body
   * scaled to 96 arena units tall - roughly 150 arena units from the centre in
   * every direction. The lane used to run to x = 1150 and y = 645, so on an
   * 1280x720 arena the circle ran off the right edge and the bottom edge, which
   * is what made the final form look cropped.
   *
   * The centre and the amplitudes below keep the whole envelope - centre plus
   * amplitude plus reach - inside the arena on both axes. The separation from
   * the ship stays HORIZONTAL, which is what the disjointness test actually
   * requires, so raising the lane does not put the pet over the ship.
   */
  flight: Object.freeze({
    centerXFraction: 0.8,
    centerYFraction: 0.72,
    amplitudeX: 80,
    amplitudeY: 26,
    periodSeconds: 9,
    /**
     * The FALLBACK muzzle spacing, arena units.
     *
     * Used only for a definition `PET_MUZZLE_LAYOUT` has never heard of, which
     * is a newly seeded or partly deployed pet. Every live pet states where its
     * guns actually are instead - see the layout table for why a single number
     * could never be right for four different bodies.
     */
    muzzleSpread: 22,
    /** Radians per second the body turns toward its target. */
    turnRadPerSecond: 7,
  }),
} as const);

/* -------------------------------------------------------------------------- */
/*  Where the guns are                                                        */
/* -------------------------------------------------------------------------- */

/**
 * One gun's position on a pet, relative to the pet's own centre.
 *
 * In ARENA UNITS, not model units, and along the FIRING LINE rather than along
 * the screen: `forward` is toward whatever the pet is aiming at and `lateral`
 * is across it, positive to starboard. That is the frame the simulation
 * already thinks in, and it means a pet that has turned still fires from its
 * own barrels rather than from a fixed screen offset.
 */
export interface PetMuzzleOffset {
  readonly lateral: number;
  readonly forward: number;
}

/**
 * Where every pet's guns ACTUALLY are, per form.
 *
 * ## Why this table has to exist
 *
 * A projectile's birth position is simulation state: it goes into the snapshot,
 * the server re-simulates from it, and two clients must agree on it exactly. So
 * it cannot be read from the 3D model, which lives in the web app and is
 * presentation. For a long time the simulation therefore used a formula - an
 * evenly spaced row 22 units apart, on the pet's centre line - and that formula
 * was wrong for every pet at once:
 *
 *   | Pet, form            | Guns on the model | Where shots were born |
 *   | -------------------- | ----------------- | --------------------- |
 *   | Sentinel Orb MK III  | +/-40, 59 forward | +/-22, 0 forward      |
 *   | Abyssal Drone EV.III | +/-39, 15 back    | +/-22, 0              |
 *   | Ballistic Drone EV.III | +/-94, 14 fwd   | +/-33, 0              |
 *
 * Nobody noticed while the pets were small. Correcting the Ballistic Drone's
 * declared height doubled its drawn size, which doubled the gap with it - the
 * launchers went out to the wingtips and the missiles kept appearing out of
 * the middle of the fuselage.
 *
 * ## How the numbers were got, and how they stay honest
 *
 * Measured off the models: each hardpoint's world position at rest, scaled by
 * the same `96 / height` the arena renderer uses. They are stable to under one
 * unit across the animation, because guns are mounted on bodies rather than on
 * anything that flaps.
 *
 * `pet-muzzles.test.ts` in the web app projects the real hardpoints and asserts
 * they still match these numbers, so moving a launcher in a model without
 * updating this table fails a test instead of shipping.
 */
export const PET_MUZZLE_LAYOUT: Readonly<Record<string, readonly (readonly PetMuzzleOffset[])[]>> =
  Object.freeze({
    /** Sentinel Orb: emitters out in front, on the ring. */
    pet_tech_sphere_legendary: Object.freeze([
      Object.freeze([{ lateral: 0, forward: 49.9 }]),
      Object.freeze([
        { lateral: -33.5, forward: 49.8 },
        { lateral: 33.5, forward: 49.8 },
      ]),
      Object.freeze([
        { lateral: -39.9, forward: 59.3 },
        { lateral: 0, forward: 70.7 },
        { lateral: 39.9, forward: 59.3 },
      ]),
    ]),
    /**
     * Abyssal Drone: cannon mouths set BEHIND the body's centre, which is why
     * `forward` is negative for the pair - the balls leave from under the
     * carapace rather than from a nose it does not have.
     */
    pet_alien_drone_excellent: Object.freeze([
      Object.freeze([{ lateral: 0, forward: -8.6 }]),
      Object.freeze([
        { lateral: -27.4, forward: -9 },
        { lateral: 27.4, forward: -9 },
      ]),
      Object.freeze([
        { lateral: -38.8, forward: -15.3 },
        { lateral: 38.8, forward: -15.3 },
        { lateral: 0, forward: 15.5 },
      ]),
    ]),
    /**
     * Ballistic Drone: the widest of the four, and the reason this table exists.
     * EV. III's order is the launcher order - forward pair first, then the pair
     * out on the flanks - so missile `i` really does leave pod `i`.
     */
    pet_missile_drone_perfect: Object.freeze([
      Object.freeze([{ lateral: 0, forward: 57.6 }]),
      Object.freeze([
        { lateral: -55.9, forward: 70.4 },
        { lateral: 55.9, forward: 70.4 },
      ]),
      Object.freeze([
        { lateral: -69.5, forward: 92.9 },
        { lateral: 69.5, forward: 92.9 },
        { lateral: -93.7, forward: 14.3 },
        { lateral: 93.7, forward: 14.3 },
      ]),
    ]),
    /**
     * Void Butterfly: the sapphire at her chest, and nothing else ever.
     *
     * She fires a beam rather than projectiles, so nothing is born here today -
     * but the emitter is a real hardpoint and stating it keeps every pet in one
     * table rather than making this one a special case somebody has to remember.
     */
    pet_void_butterfly_anomaly: Object.freeze([
      Object.freeze([{ lateral: 0, forward: 8.3 }]),
      Object.freeze([{ lateral: 0, forward: 9.9 }]),
      Object.freeze([{ lateral: 0, forward: 11.8 }]),
    ]),
    /**
     * Apache Omega: the barrel mouths, one entry per MACHINE GUN.
     *
     * One under the nose at EV. I; two under the wing roots at EV. II, with the
     * nose gun removed rather than kept alongside them; all three at EV. III,
     * the central one furthest forward. THREE rounds leave each of these, side
     * by side - the burst is spread perpendicular to that gun's own line of
     * fire, so this table still describes guns and not projectiles.
     *
     * The forward numbers are shorter than the model's own barrel positions
     * because this airframe is TILTED about thirty degrees toward the camera -
     * a helicopter seen flat from above is a rotor disc and nothing else. These
     * are the PROJECTED positions, which is where the barrels actually appear.
     */
    pet_apache_prismatic: Object.freeze([
      Object.freeze([{ lateral: 0, forward: 38.8 }]),
      Object.freeze([
        { lateral: -21, forward: 36.7 },
        { lateral: 21, forward: 36.7 },
      ]),
      Object.freeze([
        { lateral: 0, forward: 60 },
        { lateral: -33.8, forward: 44.9 },
        { lateral: 33.8, forward: 44.9 },
      ]),
    ]),
  });

/**
 * Where this form's guns are, with a FALLBACK rather than a throw.
 *
 * A definition the table has never heard of gets the old evenly-spaced row on
 * the centre line. Throwing here would throw inside the 16 ms tick, which is
 * the worst place for it: a newly seeded pet would stop the simulation instead
 * of firing from slightly the wrong place.
 */
export function petMuzzleOffsets(
  definitionId: string,
  evolution: number,
): readonly PetMuzzleOffset[] {
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  const known = PET_MUZZLE_LAYOUT[definitionId]?.[clamped - 1];
  if (known) {
    return known;
  }
  const count = petGunCount(definitionId, clamped);
  return Array.from({ length: count }, (_, index) => ({
    lateral: (index - (count - 1) / 2) * PET_BALANCE.flight.muzzleSpread,
    forward: 0,
  }));
}

/**
 * How many GUNS this form has, which is not how many projectiles it fires.
 *
 * For four of the five pets they are the same number, because an emitter
 * releases one shot. The Apache Omega's guns fire three parallel rounds each,
 * so its EV. III has three guns and nine projectiles - and confusing the two
 * would either give it nine barrels or three rounds.
 *
 * The muzzle layout is the authority when it knows the pet, because that table
 * IS the canonical hardpoint definition; the division is only for a definition
 * nobody has laid out yet.
 */
export function petGunCount(definitionId: string, evolution: number): number {
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  const known = PET_MUZZLE_LAYOUT[definitionId]?.[clamped - 1];
  if (known) {
    return known.length;
  }
  const perGun = petProjectilesPerGun(definitionId);
  return Math.max(1, Math.round(petProjectileCount(definitionId, clamped) / perGun));
}

/** How many parallel rounds one gun releases. One for every pet but the Apache. */
export function petProjectilesPerGun(definitionId: string): number {
  return Math.max(1, Math.trunc(petCombatProfile(definitionId).projectilesPerGun));
}

/**
 * How many projectiles a form fires per volley.
 *
 * Read from the pet's own catalog entry rather than computed, because every
 * pet has its own progression: the Sentinel Orb goes 1, 2, 3 and the Apache
 * will go 3, 6, 9. A formula here would be a second opinion about content the
 * catalog already states.
 *
 * Falls back to the evolution number for a definition the domain has never
 * heard of, which keeps a pet-less or newly-seeded run simulating rather than
 * throwing inside the tick.
 */
export function petProjectileCount(definitionId: string, evolution: number): number {
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  const definition = findPet(definitionId);
  const form = definition?.forms.find((entry) => entry.evolution === clamped);
  return form?.projectiles ?? clamped;
}

/**
 * Damage of one pet projectile.
 *
 * Takes the ship's own per-projectile damage as its input, which is what makes
 * the pet scale with the player automatically: allocated points, the equipped
 * ship's bonus and every artifact damage percentage are already inside it.
 * The food bonus is then applied once, additively.
 */
export function petBulletDamage(
  definitionId: string,
  shipDamagePerProjectile: number,
  damageFoodCount = 0,
): number {
  const base =
    shipDamagePerProjectile * petCombatProfile(definitionId).damageFractionOfShipProjectile;
  return base * (1 + petDamageBonusBps(damageFoodCount) / 10_000);
}

/** Volleys per second after training, before the technical ceiling. */
export function petVolleysPerSecond(definitionId: string, attackSpeedFoodCount = 0): number {
  return (
    petCombatProfile(definitionId).volleysPerSecond *
    (1 + petAttackSpeedBonusBps(attackSpeedFoodCount) / 10_000)
  );
}

/**
 * Projectiles per second across every emitter, CLAMPED.
 *
 * The clamp is technical and documented: training is uncapped by design, but
 * the renderer and the 16 ms tick are not, so there is a ceiling on how many
 * entities may be created per second. It sits far above anything ordinary
 * play reaches.
 */
export function petBulletsPerSecond(
  definitionId: string,
  evolution: number,
  attackSpeedFoodCount = 0,
): number {
  const raw =
    petProjectileCount(definitionId, evolution) *
    petVolleysPerSecond(definitionId, attackSpeedFoodCount);
  return Math.min(raw, PET_MAX_PROJECTILES_PER_SECOND);
}

/**
 * Damage instances a BEAM applies per second, after training.
 *
 * Zero for every pet that is not the Void Butterfly, which is the signal the
 * tick reads: a profile with no beam never enters the beam path at all.
 *
 * Deliberately UNCLAMPED. `petBulletsPerSecond` has a technical ceiling because
 * each projectile is an entity the renderer has to draw; a beam creates no
 * entities, so the only thing frequency costs is a damage number per instance -
 * and hiding an instance is exactly what this pet must never do.
 */
export function petBeamInstancesPerSecond(
  definitionId: string,
  evolution: number,
  attackSpeedFoodCount = 0,
): number {
  const beam = petCombatProfile(definitionId).beam;
  if (!beam) {
    return 0;
  }
  const clamped = Math.max(1, Math.min(PET_MAX_EVOLUTION, Math.trunc(evolution)));
  const base = beam.instancesPerSecondByEvolution[clamped - 1] ?? 0;
  return base * (1 + petAttackSpeedBonusBps(attackSpeedFoodCount) / 10_000);
}

/**
 * How many times a second this pet DAMAGES, whatever it fires.
 *
 * The one function the simulation and the Overview both go through, so a beam
 * pet cannot end up showing a cadence it does not deliver - which is the exact
 * defect the projectile pets carried until the attack-speed food was fixed.
 */
export function petAttacksPerSecond(
  definitionId: string,
  evolution: number,
  attackSpeedFoodCount = 0,
): number {
  return petCombatProfile(definitionId).beam
    ? petBeamInstancesPerSecond(definitionId, evolution, attackSpeedFoodCount)
    : petBulletsPerSecond(definitionId, evolution, attackSpeedFoodCount);
}

/** Estimated damage per second, for the Overview. */
export function petDamagePerSecond(
  definitionId: string,
  evolution: number,
  shipDamagePerProjectile: number,
  damageFoodCount = 0,
  attackSpeedFoodCount = 0,
): number {
  return (
    petBulletDamage(definitionId, shipDamagePerProjectile, damageFoodCount) *
    petAttacksPerSecond(definitionId, evolution, attackSpeedFoodCount)
  );
}

/* -------------------------------------------------------------------------- */
/*  The exact side (gate V28F.B)                                              */
/* -------------------------------------------------------------------------- */

/**
 * A pet's damage fraction as INTEGER basis points, asserted rather than rounded.
 *
 * ## Why the fractions had to become integers
 *
 * Because a ship's shot stopped being a `number`. Each incorporated Primordial
 * Heart adds 100 bps with no gameplay ceiling, so the ship's per-projectile
 * damage is an exact `bigint` in milli - and a pet's damage is defined as a
 * FRACTION of it. Multiplying a `bigint` by `0.22` is not an operation, and
 * converting the `bigint` first would throw away exactly the digits the exact
 * path exists to keep.
 *
 * Every fraction in the table is a clean two-decimal value - `0,22`, `0,24`,
 * `0,3`, `0,13`, `0,18` - so `x 10 000` is exact in basis points. The rounding
 * below only cleans up the binary representation of a decimal literal
 * (`0.22 * 10000` is `2200.0000000000002`), and the assertion is what makes
 * that a claim rather than a hope: a future fraction with more precision than
 * basis points can express fails here instead of silently losing a digit.
 */
export function petDamageFractionBps(definitionId: string): number {
  return exactBps(
    petCombatProfile(definitionId).damageFractionOfShipProjectile,
    `${definitionId} damage fraction`,
  );
}

function exactBps(fraction: number, what: string): number {
  const bps = Math.round(fraction * 10_000);
  if (Math.abs(fraction * 10_000 - bps) > 1e-6) {
    throw new RangeError(`${what} is not expressible in basis points: ${fraction}`);
  }
  return bps;
}

/**
 * One pet bullet's damage, in MILLI and exactly.
 *
 * The same formula {@link petBulletDamage} states, in the unit the arena now
 * applies damage in: a fraction of the SHIP's shot, then the food bonus once,
 * additively. Two truncations, both at the end of their own step, and no
 * conversion anywhere.
 *
 * ## The pet inherits the hearts, and that is the existing rule
 *
 * Not a new decision. A pet's damage has always been "a fraction of the ship's
 * per-projectile damage", which is what makes it scale with allocated points,
 * the equipped ship's bonus and every artifact percentage automatically. The
 * hearts are one more percentage in that same sum, so they arrive the same way.
 * Freezing the pet out would have been the change.
 */
export function petBulletDamageMilli(
  definitionId: string,
  shipDamageMilli: bigint,
  damageFoodCount = 0,
): bigint {
  const base = (shipDamageMilli * BigInt(petDamageFractionBps(definitionId))) / 10_000n;
  return (base * (10_000n + BigInt(petDamageBonusBps(damageFoodCount)))) / 10_000n;
}

/** What a pet's blast splashes onto everything else, in MILLI and exactly. */
export function petSplashDamageMilli(definitionId: string, directMilli: bigint): bigint {
  const blast = petCombatProfile(definitionId).blast;
  if (!blast) {
    return 0n;
  }
  const bps = exactBps(blast.splashFractionOfDirect, `${definitionId} splash fraction`);
  return (directMilli * BigInt(bps)) / 10_000n;
}
